// Abhängigkeitsfreie SVG-Charts (statt recharts — vermeidet den Browser-Crash).
// Bewusst schlicht: Linien-/Balkenchart mit Legende, Y-Achsen-Label und
// Hover-Tooltip über native <title>-Elemente.

export interface Series {
  key: string;
  color: string;
  dashed?: boolean;
  /** Stufenlinie: der Wert bleibt bis zum nächsten Punkt konstant und springt dort (Bedarf zählt erst am Cutover). */
  step?: boolean;
}

export interface Point {
  label: string;
  [key: string]: number | string | undefined;
}

export function LineChart({
  data,
  series,
  yLabel,
  height = 220,
  yMin,
  yMax,
  formatValue = (v) => new Intl.NumberFormat("de-DE").format(Math.round(v)),
  rotateLabels = false,
  pointTitle,
  pointHollow,
}: {
  data: Point[];
  series: Series[];
  yLabel?: string;
  height?: number;
  yMin?: number;
  yMax?: number;
  formatValue?: (v: number) => string;
  /** X-Labels schräg stellen — bei vielen Punkten, damit sie sich nicht überlagern. */
  rotateLabels?: boolean;
  /** Eigener Tooltip je Punkt (Standard: „Serie · Label: Wert"). */
  pointTitle?: (d: Point, key: string, v: number) => string;
  /** Punkt hohl zeichnen — z. B. für Gates ohne Sprung (+0). */
  pointHollow?: (d: Point, key: string) => boolean;
}) {
  const W = 640;
  const H = height;
  const padL = 56, padR = 16, padT = 12, padB = rotateLabels ? 52 : 28;
  const iw = W - padL - padR;
  const ih = H - padT - padB;

  const num = (v: number | string | undefined): number | null =>
    v === undefined || v === "" || Number.isNaN(Number(v)) ? null : Number(v);
  const allVals = data.flatMap((d) => series.map((s) => num(d[s.key])).filter((v): v is number => v !== null));
  const lo = yMin ?? Math.min(...allVals);
  const hi = yMax ?? Math.max(...allVals);
  const span = hi - lo || 1;

  const x = (i: number) => padL + (data.length <= 1 ? iw / 2 : (i / (data.length - 1)) * iw);
  const y = (v: number) => padT + ih - ((v - lo) / span) * ih;

  const ticks = 4;
  const tickVals = Array.from({ length: ticks + 1 }, (_, i) => lo + (span * i) / ticks);

  return (
    <div>
      <div className="w-full overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minWidth: 320, height: "auto" }} role="img">
          {/* Gitter + Y-Ticks */}
          {tickVals.map((tv, i) => (
            <g key={i}>
              <line x1={padL} x2={W - padR} y1={y(tv)} y2={y(tv)} stroke="var(--border-soft)" strokeDasharray="3 3" />
              <text x={padL - 8} y={y(tv) + 4} textAnchor="end" fontSize="11" fill="var(--text-dim)">{formatValue(tv)}</text>
            </g>
          ))}
          {yLabel && (
            <text x={14} y={padT + ih / 2} fontSize="11" fill="var(--text-faint)" transform={`rotate(-90 14 ${padT + ih / 2})`} textAnchor="middle">{yLabel}</text>
          )}
          {/* X-Labels */}
          {data.map((d, i) =>
            rotateLabels ? (
              <text key={i} x={x(i)} y={padT + ih + 10} textAnchor="end" fontSize="10" fill="var(--text-dim)" transform={`rotate(-40 ${x(i)} ${padT + ih + 10})`}>{d.label}</text>
            ) : (
              <text key={i} x={x(i)} y={H - 8} textAnchor="middle" fontSize="11" fill="var(--text-dim)">{d.label}</text>
            ),
          )}
          {/* Serien — nur definierte Werte zeichnen (Lücken bleiben Lücken) */}
          {series.map((s) => {
            const defined = data.map((d, i) => ({ i, v: num(d[s.key]), d })).filter((p) => p.v !== null) as { i: number; v: number; d: Point }[];
            const pts = s.step
              ? defined.flatMap((p, k) => (k === 0 ? [`${x(p.i)},${y(p.v)}`] : [`${x(p.i)},${y(defined[k - 1].v)}`, `${x(p.i)},${y(p.v)}`])).join(" ")
              : defined.map((p) => `${x(p.i)},${y(p.v)}`).join(" ");
            return (
              <g key={s.key}>
                <polyline points={pts} fill="none" stroke={s.color} strokeWidth={2} strokeDasharray={s.dashed ? "5 4" : undefined} />
                {defined.map((p) => {
                  const hollow = pointHollow?.(p.d, s.key) ?? false;
                  return (
                    <circle key={p.i} cx={x(p.i)} cy={y(p.v)} r={hollow ? 3.5 : 3} fill={hollow ? "var(--surface-card)" : s.color} stroke={s.color} strokeWidth={hollow ? 2 : 0}>
                      <title>{pointTitle ? pointTitle(p.d, s.key, p.v) : `${s.key} · ${p.d.label}: ${formatValue(p.v)}`}</title>
                    </circle>
                  );
                })}
              </g>
            );
          })}
        </svg>
      </div>
      <div className="text-dim mt-2 flex flex-wrap gap-4 text-xs">
        {series.map((s) => (
          <span key={s.key} className="flex items-center gap-1.5">
            <span className="inline-block h-2 w-4 rounded" style={{ backgroundColor: s.color }} />
            {s.key}
          </span>
        ))}
      </div>
    </div>
  );
}
